import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HubspotFieldSelect } from "./HubspotFieldSelect";
import { X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface HubspotField {
    name: string;
    label: string;
    type: string;
    description: string;
    groupName: string;
    required: boolean;
    fieldType?: string;
    externalOptions?: boolean;
    options?: Array<{ value: string; label: string }>;
}

interface HubspotDefaultFieldProps {
    hubspotField: string;
    value: string;
    onUpdate: (hubspotField: string, value: string) => void;
    onRemove: () => void;
    availableFields: HubspotField[];
}

export function HubspotDefaultField({
    hubspotField,
    value,
    onUpdate,
    onRemove,
    availableFields
}: HubspotDefaultFieldProps) {
    const selectedField = availableFields.find(f => f.name === hubspotField);
    const isConfigured = Boolean(hubspotField && value);
    const isEnumeration = selectedField?.type === "enumeration";
    const staticOptions = isEnumeration ? selectedField?.options || [] : [];
    const hasStaticOptions = staticOptions.length > 0;
    const isMultiselectEnum = selectedField?.fieldType === "checkbox";

    const normalizeOptionValue = (rawValue: string) => {
        const trimmed = rawValue.trim();
        const exactValue = staticOptions.find(option => option.value === trimmed);
        if (exactValue) return exactValue.value;

        const exactLabel = staticOptions.find(option => option.label === trimmed);
        if (exactLabel) return exactLabel.value;

        const normalized = trimmed.toLowerCase();
        return staticOptions.find(option =>
            option.value.toLowerCase() === normalized ||
            option.label.toLowerCase() === normalized
        )?.value || trimmed;
    };

    const getSingleSelectValue = () => {
        if (!value || !hasStaticOptions) return "";
        return normalizeOptionValue(value);
    };

    const getMultiselectValues = () => {
        if (!value || !hasStaticOptions) return new Set<string>();
        const separator = value.includes(";") ? ";" : ",";
        return new Set(
            value
                .split(separator)
                .map(normalizeOptionValue)
                .filter(Boolean)
        );
    };

    const handleMultiselectOptionChange = (optionValue: string, checked: boolean) => {
        const selectedValues = getMultiselectValues();
        if (checked) {
            selectedValues.add(optionValue);
        } else {
            selectedValues.delete(optionValue);
        }
        onUpdate(hubspotField, Array.from(selectedValues).join(";"));
    };

    const renderDefaultValueControl = () => {
        if (isEnumeration && hasStaticOptions && !isMultiselectEnum) {
            return (
                <select
                    value={getSingleSelectValue()}
                    onChange={(e) => onUpdate(hubspotField, e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
                    disabled={!hubspotField}
                >
                    <option value="">Select default value</option>
                    {staticOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label || option.value}
                        </option>
                    ))}
                </select>
            );
        }

        if (isEnumeration && hasStaticOptions && isMultiselectEnum) {
            const selectedValues = getMultiselectValues();
            return (
                <div className="max-h-32 overflow-y-auto rounded-md border border-input bg-background p-2">
                    {staticOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-2 py-1 text-sm">
                            <input
                                type="checkbox"
                                checked={selectedValues.has(option.value)}
                                onChange={(e) => handleMultiselectOptionChange(option.value, e.target.checked)}
                                className="h-4 w-4 rounded border-input"
                                disabled={!hubspotField}
                            />
                            <span className="truncate">{option.label || option.value}</span>
                        </label>
                    ))}
                </div>
            );
        }

        return (
            <Input
                value={value}
                onChange={(e) => onUpdate(hubspotField, e.target.value)}
                placeholder="Default value"
                className="h-9"
            />
        );
    };

    return (
        <div className={cn(
            "rounded-lg border p-3 transition-all",
            isConfigured 
                ? "bg-muted/30 border-border"
                : "border-dashed"
        )}>
            <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    <HubspotFieldSelect
                        fields={availableFields}
                        value={hubspotField}
                        onSelect={(field) => onUpdate(field, value)}
                        placeholder="Select HubSpot property"
                        simpleDisplay
                    />
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    {renderDefaultValueControl()}
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onRemove}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>
            {selectedField && (
                <p className="text-xs text-muted-foreground mt-2 ml-1">
                    <span className="capitalize">{selectedField.groupName}</span>
                    {selectedField.type && <span> • {selectedField.type}</span>}
                    {selectedField.fieldType && <span> • {selectedField.fieldType}</span>}
                    {selectedField.description && <span> — {selectedField.description}</span>}
                    {isEnumeration && hasStaticOptions && (
                        <span> — Choose by label; Aimdoc saves HubSpot option values.</span>
                    )}
                    {isEnumeration && !hasStaticOptions && (
                        <span> — HubSpot may validate this value dynamically.</span>
                    )}
                </p>
            )}
        </div>
    );
}
